import { commands, type OAuthTokens } from '@/lib/platform/generated/bindings.gen';
import { load as loadStore } from '@tauri-apps/plugin-store';

import { isNative } from '@/lib/platform';
import { message } from '@nula/core-ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

/**
 * Persistent auth storage.
 *
 * Three responsibilities:
 *
 * 1. Push the current access token into the Tauri Rust process so the
 *    native HTTP clients (calendar, recording) can attach it as
 *    `Authorization: Bearer ...` on every request.
 *
 * 2. Persist the OAuth refresh_token. On boot we read it back, POST to
 *    Auth0's /oauth/token with grant_type=refresh_token to get a fresh
 *    access_token, and hydrate `useAuthStore`. Without this the user
 *    has to re-OAuth every time they quit and reopen Nula.
 *
 * 3. Persist the last-signed-in email so we can short-circuit the
 *    Login screen with a "Last signed in as <email>" resume card.
 *
 * Desktop: tauri-plugin-store (writes JSON to the OS data dir, e.g.
 * `~/Library/Application Support/com.nula.app/nula-auth.json` on macOS).
 *
 * Web: localStorage for both the refresh_token and the last-email.
 *
 * Refresh-token storage choice (localStorage vs sessionStorage):
 *   sessionStorage is per-tab — it doesn't survive opening the same site in
 *   a new tab (e.g. clicking an invite URL from a different app), which
 *   broke the /join/<token> flow. localStorage works across tabs.
 *
 *   The marginal XSS exposure delta is small: any XSS that lands on the
 *   page can read either store equally. We rely on Auth0's refresh-token
 *   rotation + reuse-detection: a stolen token gets revoked (whole family)
 *   on the next use by either party. Combined with the Cloudflare WAF
 *   restrictions on our origin, this is the pragmatic trade-off for a SPA
 *   that can't use HttpOnly cookies.
 */

const STORE_FILE = 'nula-auth.json';
const REFRESH_TOKEN_KEY = 'refresh_token';
const WEB_REFRESH_TOKEN_KEY = 'nula_refresh_token';
const LAST_EMAIL_KEY = 'last_email';
const WEB_LAST_EMAIL_KEY = 'nula_last_email';

async function nativeStore() {
  return loadStore(STORE_FILE);
}

export async function storeRefreshToken(token: string): Promise<void> {
  if (isNative) {
    try {
      const store = await nativeStore();
      await store.set(REFRESH_TOKEN_KEY, token);
    } catch (err) {
      console.warn('[auth] storeRefreshToken store write failed (non-fatal)', err);
    }
  } else {
    localStorage.setItem(WEB_REFRESH_TOKEN_KEY, token);
  }
}

export async function getRefreshToken(): Promise<string | null> {
  if (isNative) {
    try {
      const store = await nativeStore();
      return (await store.get<string>(REFRESH_TOKEN_KEY)) ?? null;
    } catch (err) {
      console.warn('[auth] getRefreshToken store read failed (non-fatal)', err);
      return null;
    }
  }
  // Read from localStorage. Migration path for users with a pre-existing
  // sessionStorage value: if localStorage has nothing but sessionStorage
  // does, promote it. One-time, transparent to the user.
  const local = localStorage.getItem(WEB_REFRESH_TOKEN_KEY);
  if (local) return local;
  const legacy = sessionStorage.getItem(WEB_REFRESH_TOKEN_KEY);
  if (legacy) {
    localStorage.setItem(WEB_REFRESH_TOKEN_KEY, legacy);
    sessionStorage.removeItem(WEB_REFRESH_TOKEN_KEY);
    return legacy;
  }
  return null;
}

export async function clearRefreshToken(): Promise<void> {
  if (isNative) {
    try {
      const store = await nativeStore();
      await store.delete(REFRESH_TOKEN_KEY);
    } catch (err) {
      console.warn('[auth] clearRefreshToken store delete failed (non-fatal)', err);
    }
  } else {
    localStorage.removeItem(WEB_REFRESH_TOKEN_KEY);
    // Defensive: drop the legacy sessionStorage entry too. Harmless if absent.
    sessionStorage.removeItem(WEB_REFRESH_TOKEN_KEY);
  }
}

// ── Last-signed-in email — UX convenience, not a credential ─────────────
// Web uses localStorage (we WANT it to survive tab closes); desktop uses the
// same tauri-plugin-store as the refresh token. Read errors swallow + return
// null so a corrupted store never crashes the login screen.

export async function storeLastEmail(email: string): Promise<void> {
  if (isNative) {
    try {
      const store = await nativeStore();
      await store.set(LAST_EMAIL_KEY, email);
    } catch (err) {
      console.warn('[auth] storeLastEmail store write failed (non-fatal)', err);
    }
  } else {
    localStorage.setItem(WEB_LAST_EMAIL_KEY, email);
  }
}

export async function getLastEmail(): Promise<string | null> {
  if (isNative) {
    try {
      const store = await nativeStore();
      return (await store.get<string>(LAST_EMAIL_KEY)) ?? null;
    } catch (err) {
      console.warn('[auth] getLastEmail store read failed (non-fatal)', err);
      return null;
    }
  }
  return localStorage.getItem(WEB_LAST_EMAIL_KEY);
}

export async function clearLastEmail(): Promise<void> {
  if (isNative) {
    try {
      const store = await nativeStore();
      await store.delete(LAST_EMAIL_KEY);
    } catch (err) {
      console.warn('[auth] clearLastEmail store delete failed (non-fatal)', err);
    }
  } else {
    localStorage.removeItem(WEB_LAST_EMAIL_KEY);
  }
}

export async function setAccessTokenOnService(
  token: string,
): Promise<void> {
  if (!token) return;
  if (!isNative) return;
  const calRes = await commands.setCalendarAuthToken(token);
  if (calRes.status === 'error') {
    console.warn('[auth] set_calendar_auth_token failed (non-fatal)', calRes.error);
  }
  const recRes = await commands.setRecordingAuthToken(token);
  if (recRes.status === 'error') {
    console.warn('[auth] set_recording_auth_token failed (non-fatal)', recRes.error);
    void message.warning('Recording uploads may fail — please re-authenticate');
  }
  const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
  if (workspaceId) {
    commands.setRecordingWorkspaceId(workspaceId).then((res) => {
      if (res.status === 'error') {
        console.warn('[auth] set_recording_workspace_id failed (non-fatal)', res.error);
      }
    }).catch((e) => {
      console.warn('[auth] set_recording_workspace_id failed (non-fatal)', e);
    });
  }
}

/**
 * Apply a token set minted by a background refresh: re-push the access token to
 * the native HTTP clients, update the auth store's token, and persist the
 * rotated refresh token. Unlike the sign-in flow, this does NOT re-fetch
 * userinfo — the signed-in user is unchanged, only the token rotates.
 */
export async function applyRefreshedTokens(tokens: OAuthTokens): Promise<void> {
  await setAccessTokenOnService(tokens.access_token);
  useAuthStore.getState().setAccessToken(tokens.access_token);
  if (tokens.refresh_token) {
    await storeRefreshToken(tokens.refresh_token);
  }
}

export async function clearAccessTokenOnService(): Promise<void> {
  if (!isNative) return;
  const calRes = await commands.setCalendarAuthToken(null);
  if (calRes.status === 'error') {
    console.warn('[auth] set_calendar_auth_token (clear) failed (non-fatal)', calRes.error);
  }
  const recRes = await commands.setRecordingAuthToken(null);
  if (recRes.status === 'error') {
    console.warn('[auth] set_recording_auth_token (clear) failed (non-fatal)', recRes.error);
  }
  commands.setRecordingWorkspaceId(null).then((res) => {
    if (res.status === 'error') {
      console.warn('[auth] set_recording_workspace_id (clear) failed (non-fatal)', res.error);
    }
  }).catch((e) => {
    console.warn('[auth] set_recording_workspace_id (clear) failed (non-fatal)', e);
  });
}
